// #587: Bevorzugte Zustellwege am Kontakt – Mehrfachauswahl E-Mail / Post / Portal (Portal nur Mieter/Eigentümer).
// Etappe 3: Sperrvermerk (Freitext) – solange gesetzt, schickt der Schreiben-Assistent nichts an diesen Kontakt.
// Speichert sofort (unabhängig vom Bearbeiten-Formular); der Schreiben-Assistent übernimmt beides.
import { useEffect, useState } from 'react';
import { Prohibit } from '@phosphor-icons/react';
import { supabase } from '../../lib/supabase/client';
import { KONTAKT_ZUSTELLWEGE, ZUSTELLWEG_LABEL, type EmpfaengerArt, type Zustellweg } from '../../lib/schreiben/typen';

const TABELLE: Record<EmpfaengerArt, string> = { mieter: 'mieter', eigentuemer: 'eigentuemer', kontakt: 'kontakte', dienstleister: 'dienstleister' };

export function ZustellwegeAuswahl({ art, id }: { art: EmpfaengerArt; id: string }) {
  const [wege, setWege] = useState<Zustellweg[] | null>(null);
  /** undefined = Spalte fehlt noch (Migration Etappe 3) */
  const [vermerk, setVermerk] = useState<string | undefined>(undefined);
  const [entwurf, setEntwurf] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const angebot = KONTAKT_ZUSTELLWEGE.filter((w) => w !== 'portal' || art === 'mieter' || art === 'eigentuemer');

  useEffect(() => {
    let aktiv = true;
    void (async () => {
      let r = await supabase.from(TABELLE[art]).select('zustellwege, sperrvermerk').eq('id', id).maybeSingle();
      const mitVermerk = !r.error;
      if (r.error) r = await supabase.from(TABELLE[art]).select('zustellwege').eq('id', id).maybeSingle() as typeof r;
      if (!aktiv) return;
      const d = r.data as { zustellwege?: Zustellweg[]; sperrvermerk?: string | null } | null;
      setWege(r.error ? null : (d?.zustellwege ?? []));
      const v = mitVermerk ? (d?.sperrvermerk ?? '') : undefined;
      setVermerk(v); setEntwurf(v ?? '');
    })();
    return () => { aktiv = false; };
  }, [art, id]);

  const umschalten = (w: Zustellweg) => {
    if (!wege) return;
    const alt = wege;
    const neu = KONTAKT_ZUSTELLWEGE.filter((x) => (x === w ? !alt.includes(x) : alt.includes(x)));
    setFehler(null); setWege(neu);
    void supabase.from(TABELLE[art]).update({ zustellwege: neu }).eq('id', id)
      .then(({ error }) => { if (error) { setWege(alt); setFehler(error.message); } });
  };

  const vermerkSpeichern = (wert: string) => {
    const alt = vermerk;
    setFehler(null); setVermerk(wert.trim()); setEntwurf(wert.trim());
    void supabase.from(TABELLE[art]).update({ sperrvermerk: wert.trim() || null }).eq('id', id)
      .then(({ error }) => { if (error) { setVermerk(alt); setEntwurf(alt ?? ''); setFehler(error.message); } });
  };

  if (wege === null) return null; // Spalte noch nicht da (Migration) oder lädt
  return (
    <div className="flex flex-col" style={{ gap: 6 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Bevorzugte Zustellwege</span>
      <div className="flex flex-wrap" role="group" aria-label="Bevorzugte Zustellwege" style={{ gap: 6 }}>
        {angebot.map((w) => {
          const an = wege.includes(w);
          return (
            <button key={w} type="button" aria-pressed={an} onClick={() => umschalten(w)}
              style={{ height: 36, padding: '0 14px', borderRadius: 12, border: 'none', fontSize: 13, fontWeight: 800, cursor: 'pointer',
                background: an ? 'var(--acc-fill)' : 'var(--fill2)', color: an ? 'var(--acc-ink)' : 'var(--ink2)' }}>
              {ZUSTELLWEG_LABEL[w]}
            </button>
          );
        })}
      </div>
      <span style={{ fontSize: 12, color: 'var(--ink3)' }}>
        {wege.length ? 'Wird bei jedem neuen Schreiben vorausgewählt.' : 'Nichts gewählt: automatisch E-Mail, wenn vorhanden, sonst Post.'}
      </span>
      {vermerk !== undefined && (
        <div className="flex flex-col" style={{ gap: 6, marginTop: 4 }}>
          <span className="flex items-center" style={{ gap: 6, fontSize: 12, fontWeight: 700, color: vermerk ? 'var(--bad)' : 'var(--ink3)' }}>
            <Prohibit size={13} weight="bold" />Sperrvermerk{vermerk ? ' – Schreiben an diesen Kontakt sind gesperrt' : ''}
          </span>
          <div className="flex flex-wrap items-center" style={{ gap: 6 }}>
            <input value={entwurf} onChange={(e) => setEntwurf(e.target.value)} aria-label="Sperrvermerk"
              onKeyDown={(e) => { if (e.key === 'Enter' && entwurf.trim() !== vermerk) vermerkSpeichern(entwurf); }}
              placeholder="z. B. nur über Rechtsanwalt Klein, verstorben, Betreuung …"
              style={{ flex: '1 1 220px', minWidth: 0, height: 36, padding: '0 12px', borderRadius: 12, border: 'none', fontSize: 13, fontFamily: 'inherit',
                background: vermerk ? 'var(--bad-soft, var(--fill2))' : 'var(--fill2)', color: 'var(--ink)', outline: 'none' }} />
            {entwurf.trim() !== vermerk && (
              <button type="button" onClick={() => vermerkSpeichern(entwurf)}
                style={{ height: 36, padding: '0 14px', borderRadius: 12, border: 'none', fontSize: 13, fontWeight: 800, cursor: 'pointer', background: 'var(--acc-fill)', color: 'var(--acc-ink)' }}>
                {entwurf.trim() ? 'Vermerk speichern' : 'Vermerk aufheben'}
              </button>
            )}
          </div>
        </div>
      )}
      {fehler && <span role="alert" style={{ fontSize: 12, color: 'var(--bad)' }}>Nicht gespeichert: {fehler}</span>}
    </div>
  );
}
